import React, { useState, useRef } from 'react';
import { Modal } from 'antd';
import './index.scss';

export interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
  thumbnail?: string;
  name?: string;
}

interface MediaPreviewProps {
  items: MediaItem[];
  maxDisplay?: number;
  size?: 'small' | 'medium' | 'large';
  onPreview?: (item: MediaItem, index: number) => void;
}

const MediaPreview: React.FC<MediaPreviewProps> = ({
  items = [],
  maxDisplay = 4,
  size = 'medium',
  onPreview,
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState<MediaItem | null>(null);

  if (!items || items.length === 0) {
    return null;
  }

  const displayItems = items.slice(0, maxDisplay);
  const remainingCount = items.length - maxDisplay;

  const handleItemClick = (item: MediaItem, index: number) => {
    setCurrentItem(item);
    setPreviewVisible(true);
    onPreview?.(item, index);
  };

  const handleModalClose = () => {
    setPreviewVisible(false);
    setCurrentItem(null);
  };

  return (
    <>
      <div className={`media-preview ${size}`}>
        {displayItems.map((item, index) => (
          <div
            key={item.id}
            className="media-item"
            onClick={() => handleItemClick(item, index)}
          >
            {item.type === 'image' ? (
              <img
                src={item.thumbnail || item.url}
                alt={item.name || `图片${index + 1}`}
                className="media-thumbnail"
              />
            ) : (
              <div className="video-thumbnail">
                {item.thumbnail ? (
                  <img
                    src={item.thumbnail}
                    alt={item.name || `视频${index + 1}`}
                    className="media-thumbnail"
                  />
                ) : (
                  <div className="video-placeholder">
                    <div className="video-icon">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                      ></svg>
                    </div>
                  </div>
                )}
                <div className="video-play-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="rgba(0,0,0,0.6)" />
                    <polygon points="10,8 16,12 10,16" fill="white" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ))}

        {remainingCount > 0 && (
          <div className="media-item more-indicator">
            <div className="more-count">+{remainingCount}</div>
          </div>
        )}
      </div>

      <Modal
        open={previewVisible}
        onCancel={handleModalClose}
        destroyOnClose
        footer={null}
        width="46%"
        centered
        className="media-preview-modal"
      >
        {currentItem && (
          <div className="modal-content">
            {currentItem.type === 'image' ? (
              <img
                src={currentItem.url}
                alt={currentItem.name}
                style={{ width: '100%', height: 'auto' }}
              />
            ) : (
              <video
                src={currentItem.url}
                controls
                style={{ width: '100%', height: 'auto' }}
              >
                您的浏览器不支持视频播放
              </video>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default React.memo(MediaPreview);
